import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, RefreshCw, UserX, UserCheck, KeyRound, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin User Management panel.
 * Lists users, exposes tier override, suspend/unsuspend, send password reset,
 * and soft delete. Auth: expects an admin Bearer token from the parent dashboard.
 */
export function AdminUserManagement({ token }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [tier, setTier] = useState('');
  const [includeSuspended, setIncludeSuspended] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const limit = 25;

  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (tier) params.set('tier', tier);
      if (includeSuspended) params.set('include_suspended', 'true');
      params.set('limit', limit);
      params.set('offset', offset);
      const res = await axios.get(`${API_URL}/api/admin/users?${params.toString()}`, { headers });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [q, tier, includeSuspended, offset, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openDetail = async (uid) => {
    setSelected(uid);
    setDetail(null);
    setDetailLoading(true);
    setAuditLog([]);
    try {
      const res = await axios.get(`${API_URL}/api/admin/users/${uid}`, { headers });
      setDetail(res.data);
      loadAuditLog(uid);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load user');
    } finally {
      setDetailLoading(false);
    }
  };

  const applyUpdate = async (uid, body, successMsg) => {
    try {
      const res = await axios.put(`${API_URL}/api/admin/users/${uid}`, body, { headers });
      toast.success(successMsg);
      setDetail((d) => (d ? { ...d, user: res.data.user } : d));
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    }
  };

  const handleChangeTier = (uid, newTier) => {
    if (!window.confirm(`Change this user's tier to ${newTier}? This overrides Stripe and resets the allowance window.`)) return;
    const expiry = window.prompt('Tier expiry date (YYYY-MM-DD) — leave blank for permanent:', '');
    const body = { tier: newTier };
    if (expiry && expiry.trim()) {
      body.tier_expires_at = new Date(expiry.trim() + 'T23:59:59Z').toISOString();
    }
    applyUpdate(uid, body, `Tier updated to ${newTier}`);
  };

  const handleAddCredits = async (uid) => {
    const n = window.prompt('How many download credits to add? (1-50)', '1');
    if (!n) return;
    const credits = parseInt(n, 10);
    if (!credits || credits < 1 || credits > 50) { toast.error('Enter a number between 1 and 50'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/${uid}/add-download-credits`,
        { credits }, { headers });
      toast.success(`Added ${credits} download credit${credits === 1 ? '' : 's'}`);
      setDetail((d) => (d ? { ...d, user: res.data.user } : d));
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.error || 'Add credits failed'); }
  };

  const handleResetDownloads = async (uid) => {
    if (!window.confirm('Reset downloads used to 0? (cap unchanged)')) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/${uid}/reset-downloads`, {}, { headers });
      toast.success('Downloads allowance reset');
      setDetail((d) => (d ? { ...d, user: res.data.user } : d));
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.error || 'Reset failed'); }
  };

  const handleResetQs = async (uid) => {
    if (!window.confirm('Reset QS consultations back to the full annual cap?')) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/${uid}/reset-qs-consultations`, {}, { headers });
      toast.success('QS consultations reset');
      setDetail((d) => (d ? { ...d, user: res.data.user } : d));
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.error || 'Reset failed'); }
  };

  const handleGiftTier = async (uid) => {
    const tier = window.prompt('Gift which tier? (pro or pro_plus)', 'pro_plus');
    if (!tier || !['pro', 'pro_plus'].includes(tier)) return;
    const days = parseInt(window.prompt('For how many days? (1-730)', '30'), 10);
    if (!days || days < 1 || days > 730) { toast.error('Enter a number between 1 and 730'); return; }
    const sendEmail = window.confirm('Send notification email to the user?');
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/${uid}/gift-tier`,
        { tier, days, send_email: sendEmail }, { headers });
      toast.success(`${tier === 'pro_plus' ? 'Pro+' : 'Pro'} gifted for ${days} days`);
      setDetail((d) => (d ? { ...d, user: res.data.user } : d));
      loadAuditLog(uid);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.error || 'Gift failed'); }
  };

  const [auditLog, setAuditLog] = useState([]);
  const loadAuditLog = async (uid) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users/${uid}/audit-log`, { headers });
      setAuditLog(res.data.entries || []);
    } catch (err) { setAuditLog([]); }
  };

  const handleSuspend = (uid, suspend) => {
    const reason = suspend ? window.prompt('Suspension reason (optional):', '') : null;
    applyUpdate(uid, { suspended: suspend, suspended_reason: reason || '' },
      suspend ? 'User suspended' : 'User restored');
  };

  const handleReset = async (uid) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${uid}/send-password-reset`, {}, { headers });
      toast.success('Password reset email sent');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reset failed');
    }
  };

  const [deleteModal, setDeleteModal] = useState(null);  // null | { uid, email, mode, typedConfirm, submitting }

  const openDeleteModal = (uid) => {
    setDeleteModal({
      uid,
      email: detail?.user?.email || '',
      mode: 'soft',          // 'soft' | 'hard'
      typedConfirm: '',
      submitting: false,
    });
  };

  const closeDeleteModal = () => setDeleteModal(null);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    const { uid, mode, typedConfirm } = deleteModal;
    if (mode === 'hard' && typedConfirm !== 'DELETE') {
      toast.error('Type DELETE in capitals to confirm GDPR erasure.');
      return;
    }
    setDeleteModal((m) => ({ ...m, submitting: true }));
    try {
      if (mode === 'hard') {
        await axios.delete(`${API_URL}/api/admin/users/${uid}?hard=true`, { headers });
        toast.success('GDPR deletion complete — confirmation email sent');
      } else {
        await axios.delete(`${API_URL}/api/admin/users/${uid}`, { headers });
        toast.success('User soft-deleted');
      }
      closeDeleteModal();
      setSelected(null); setDetail(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
      setDeleteModal((m) => (m ? { ...m, submitting: false } : null));
    }
  };

  const handleDelete = (uid) => openDeleteModal(uid);

  return (
    <div style={{ padding: 24 }} data-testid="admin-user-management">
      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
        <div style={{ position: 'relative', flex: '1 1 300px' }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: 11, color: '#94A3B8' }} />
          <input
            type="text"
            placeholder="Search by email…"
            value={q}
            onChange={(e) => { setOffset(0); setQ(e.target.value); }}
            data-testid="admin-users-search"
            style={{
              width: '100%', padding: '9px 12px 9px 34px', fontSize: 13,
              border: '1px solid #E2E8F0', borderRadius: 8, background: '#fff',
            }}
          />
        </div>
        <select
          value={tier}
          onChange={(e) => { setOffset(0); setTier(e.target.value); }}
          data-testid="admin-users-tier-filter"
          style={{ padding: '9px 12px', fontSize: 13, border: '1px solid #E2E8F0', borderRadius: 8, background: '#fff' }}
        >
          <option value="">All tiers</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="pro_plus">Pro+</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569' }}>
          <input
            type="checkbox"
            checked={includeSuspended}
            onChange={(e) => { setOffset(0); setIncludeSuspended(e.target.checked); }}
            data-testid="admin-users-include-suspended"
          />
          Include suspended
        </label>
        <button onClick={fetchUsers} className="btn-secondary" data-testid="admin-users-refresh"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', fontSize: 13 }}>
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#0F2742', color: '#fff' }}>
              <th style={{ textAlign: 'left', padding: '12px 14px', fontSize: 11, letterSpacing: '.08em' }}>Email</th>
              <th style={{ textAlign: 'left', padding: '12px 14px', fontSize: 11, letterSpacing: '.08em' }}>Tier</th>
              <th style={{ textAlign: 'left', padding: '12px 14px', fontSize: 11, letterSpacing: '.08em' }}>Status</th>
              <th style={{ textAlign: 'left', padding: '12px 14px', fontSize: 11, letterSpacing: '.08em' }}>Downloads</th>
              <th style={{ textAlign: 'left', padding: '12px 14px', fontSize: 11, letterSpacing: '.08em' }}>QS Calls</th>
              <th style={{ textAlign: 'left', padding: '12px 14px', fontSize: 11, letterSpacing: '.08em' }}>Created</th>
              <th style={{ width: 30 }} />
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && !loading && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#94A3B8', fontStyle: 'italic' }}>
                No users match those filters.
              </td></tr>
            )}
            {users.map((u) => (
              <tr
                key={u.user_id}
                onClick={() => openDetail(u.user_id)}
                data-testid={`admin-user-row-${u.user_id}`}
                style={{
                  borderTop: '1px solid #F1F5F9',
                  cursor: 'pointer',
                  background: selected === u.user_id ? '#F0F9FF' : 'transparent',
                }}
              >
                <td style={{ padding: '10px 14px', color: '#0F2742', fontWeight: 500 }}>
                  {u.email}
                  {u.suspended_at && (
                    <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, letterSpacing: '.06em',
                                   background: '#FEE2E2', color: '#991B1B', padding: '2px 6px',
                                   borderRadius: 4, textTransform: 'uppercase' }}>Suspended</span>
                  )}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                    padding: '3px 8px', borderRadius: 4,
                    background: u.tier === 'pro_plus' ? '#DBEAFE' : u.tier === 'pro' ? '#ECFDF5' : '#F1F5F9',
                    color:      u.tier === 'pro_plus' ? '#1E40AF' : u.tier === 'pro' ? '#065F46' : '#475569',
                  }}>{u.tier === 'pro_plus' ? 'Pro+' : u.tier}</span>
                </td>
                <td style={{ padding: '10px 14px', color: '#64748B' }}>{u.subscription_status || '—'}</td>
                <td style={{ padding: '10px 14px', color: '#64748B' }}>
                  {u.downloads_cap > 0 ? `${u.downloads_used}/${u.downloads_cap}` : '—'}
                </td>
                <td style={{ padding: '10px 14px', color: '#64748B' }}>
                  {u.quarterly_calls_cap > 0 ? `${u.quarterly_calls_remaining}/${u.quarterly_calls_cap}` : '—'}
                </td>
                <td style={{ padding: '10px 14px', color: '#64748B' }}>
                  {u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <ChevronRight size={14} style={{ color: '#94A3B8' }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12,
                    fontSize: 12, color: '#64748B' }}>
        <div>Showing {users.length ? offset + 1 : 0}–{offset + users.length} of {total}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}
                  className="btn-secondary" style={{ fontSize: 12, padding: '6px 12px' }}
                  data-testid="admin-users-prev-page">Prev</button>
          <button disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}
                  className="btn-secondary" style={{ fontSize: 12, padding: '6px 12px' }}
                  data-testid="admin-users-next-page">Next</button>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(520px, 100%)', background: '#fff',
          boxShadow: '-8px 0 24px rgba(15,39,66,.1)', zIndex: 100, overflowY: 'auto',
        }} data-testid="admin-user-detail-drawer">
          <div style={{ padding: 20, borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#64748B', letterSpacing: '.1em', textTransform: 'uppercase' }}>
                User detail
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0F2742', marginTop: 4 }}>
                {detail?.user?.email || 'Loading…'}
              </div>
            </div>
            <button onClick={() => { setSelected(null); setDetail(null); }}
                    data-testid="admin-user-close-drawer"
                    style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#64748B' }}>
              ×
            </button>
          </div>

          {detailLoading && <div style={{ padding: 28, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>Loading…</div>}

          {detail && !detailLoading && (
            <div style={{ padding: 20 }}>
              {/* Actions */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                <select
                  value={detail.user.tier}
                  onChange={(e) => handleChangeTier(detail.user.user_id, e.target.value)}
                  data-testid="admin-user-tier-select"
                  style={{ padding: '8px 10px', fontSize: 12, border: '1px solid #E2E8F0', borderRadius: 8 }}
                >
                  <option value="free">Free</option>
                  <option value="pro">Pro</option>
                  <option value="pro_plus">Pro+</option>
                </select>
                {detail.user.suspended_at ? (
                  <button onClick={() => handleSuspend(detail.user.user_id, false)}
                          className="btn-secondary" data-testid="admin-user-restore"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '8px 12px' }}>
                    <UserCheck size={13} /> Restore
                  </button>
                ) : (
                  <button onClick={() => handleSuspend(detail.user.user_id, true)}
                          className="btn-secondary" data-testid="admin-user-suspend"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '8px 12px', color: '#991B1B' }}>
                    <UserX size={13} /> Suspend
                  </button>
                )}
                <button onClick={() => handleReset(detail.user.user_id)}
                        className="btn-secondary" data-testid="admin-user-reset-password"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '8px 12px' }}>
                  <KeyRound size={13} /> Send reset link
                </button>
                <button onClick={() => handleDelete(detail.user.user_id)}
                        className="btn-secondary" data-testid="admin-user-delete"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '8px 12px', color: '#991B1B' }}>
                  Delete / GDPR erase
                </button>
              </div>

              {/* Granular allowance + gift actions */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                <button onClick={() => handleAddCredits(detail.user.user_id)}
                        data-testid="admin-user-add-credits"
                        className="btn-secondary"
                        style={{ fontSize: 12, padding: '8px 12px' }}>
                  + Add download credits
                </button>
                <button onClick={() => handleResetDownloads(detail.user.user_id)}
                        data-testid="admin-user-reset-downloads"
                        className="btn-secondary"
                        style={{ fontSize: 12, padding: '8px 12px' }}>
                  Reset downloads
                </button>
                <button onClick={() => handleResetQs(detail.user.user_id)}
                        data-testid="admin-user-reset-qs"
                        className="btn-secondary"
                        style={{ fontSize: 12, padding: '8px 12px' }}>
                  Reset QS consultations
                </button>
                <button onClick={() => handleGiftTier(detail.user.user_id)}
                        data-testid="admin-user-gift-tier"
                        className="btn-secondary"
                        style={{ fontSize: 12, padding: '8px 12px', background: '#EFF6FF', color: '#1D4ED8', borderColor: '#BFDBFE' }}>
                  🎁 Gift Pro / Pro+
                </button>
              </div>

              {/* Metadata */}
              <div style={{ background: '#F7F8FA', padding: 14, borderRadius: 8, fontSize: 12, marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 5 }}>
                  <div style={{ color: '#64748B' }}>Tier</div><div>{detail.user.tier}</div>
                  <div style={{ color: '#64748B' }}>Subscription</div><div>{detail.user.subscription_status || '—'}</div>
                  <div style={{ color: '#64748B' }}>Downloads</div><div>{detail.user.downloads_used}/{detail.user.downloads_cap}</div>
                  <div style={{ color: '#64748B' }}>QS consultations</div><div>{detail.user.quarterly_calls_remaining}/{detail.user.quarterly_calls_cap} per year</div>
                  <div style={{ color: '#64748B' }}>Stripe customer</div><div>{detail.user.has_stripe_customer ? 'Yes' : 'No'}</div>
                  <div style={{ color: '#64748B' }}>Marketing consent</div><div>{detail.user.marketing_consent ? 'Yes' : 'No'}</div>
                  <div style={{ color: '#64748B' }}>Created</div><div>{detail.user.created_at ? new Date(detail.user.created_at).toLocaleString('en-GB') : '—'}</div>
                  {detail.user.tier_expires_at && (
                    <>
                      <div style={{ color: '#64748B' }}>Tier expires</div>
                      <div style={{ color: '#B45309' }} data-testid="admin-user-tier-expires">
                        {new Date(detail.user.tier_expires_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    </>
                  )}
                  {detail.user.suspended_at && (
                    <>
                      <div style={{ color: '#64748B' }}>Suspended</div>
                      <div style={{ color: '#991B1B' }}>{new Date(detail.user.suspended_at).toLocaleString('en-GB')} — {detail.user.suspended_reason || '(no reason given)'}</div>
                    </>
                  )}
                </div>
              </div>

              {/* Orders */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#64748B', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                  Orders ({detail.orders.length})
                </div>
                {detail.orders.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#94A3B8', fontStyle: 'italic' }}>No orders yet.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 6 }}>
                    {detail.orders.slice(0, 10).map((o) => (
                      <div key={o.project_id} style={{ fontSize: 12, padding: 8, background: '#F7F8FA', borderRadius: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontWeight: 600 }}>{o.order_ref}</span>
                          <span style={{ color: '#64748B' }}>{o.product_type} · {o.status}</span>
                        </div>
                        {o.address && <div style={{ color: '#64748B', fontSize: 11, marginTop: 2 }}>{o.address}</div>}
                        {o.contractor_quote?.amount_gbp && (
                          <div
                            data-testid={`admin-order-contractor-quote-${o.project_id}`}
                            style={{ marginTop: 4, padding: '4px 8px', background: '#FEF3C7', borderRadius: 4, fontSize: 11, color: '#92400E' }}
                          >
                            <strong>Contractor quote:</strong> £{o.contractor_quote.amount_gbp.toLocaleString()}
                            {o.contractor_quote.trade && o.contractor_quote.trade !== 'overall' && ` · ${o.contractor_quote.trade}`}
                            {o.contractor_quote.notes && (
                              <div style={{ fontStyle: 'italic', marginTop: 2 }}>"{o.contractor_quote.notes}"</div>
                            )}
                          </div>
                        )}
                        {/* Admin resend fulfillment — recovers customers stranded by SMTP/S3 outage */}
                        {o.status === 'paid' && ['detailed', 'scope_materials', 'scope_sow', 'scope_full'].includes(o.product_type) && (
                          <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              data-testid={`admin-resend-fulfillment-${o.project_id}`}
                              onClick={async () => {
                                if (!window.confirm(`Resend fulfillment for ${o.order_ref}?\n\nThis will regenerate PDFs and re-send the email to ${detail?.user?.email || 'the customer'}.`)) return;
                                try {
                                  await axios.post(
                                    `${API_URL}/api/admin/projects/${o.project_id}/resend-fulfillment`,
                                    {},
                                    { headers },
                                  );
                                  toast.success(`Resent fulfillment for ${o.order_ref}`);
                                } catch (err) {
                                  toast.error(err.response?.data?.error || 'Resend failed — check backend logs');
                                }
                              }}
                              style={{
                                background: '#1D4ED8', color: '#fff', border: 'none',
                                padding: '4px 10px', fontSize: 10, fontWeight: 700,
                                borderRadius: 4, cursor: 'pointer',
                              }}
                            >
                              Resend email + PDF
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Drafts */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#64748B', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                  Drafts ({detail.drafts.length})
                </div>
                {detail.drafts.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#94A3B8', fontStyle: 'italic' }}>No active drafts.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 6 }}>
                    {detail.drafts.slice(0, 10).map((d) => (
                      <div key={d.draft_id} style={{ fontSize: 12, padding: 8, background: '#F7F8FA', borderRadius: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontWeight: 600 }}>{d.draft_id.slice(0, 8)}…</span>
                          <span style={{ color: '#64748B' }}>
                            {d.method} · {d.status}
                            {d.documents_paid && <span style={{ marginLeft: 6, color: '#059669', fontWeight: 700 }}>· paid</span>}
                          </span>
                        </div>
                        {d.address && <div style={{ color: '#64748B', fontSize: 11, marginTop: 2 }}>{d.address}</div>}
                        {(d.checkout_recovery_sent || d.checkout_recovery_d3_sent) && (
                          <div style={{ color: '#1D4ED8', fontSize: 10, marginTop: 4 }}>
                            Recovery: {d.checkout_recovery_sent && 'D+1'} {d.checkout_recovery_d3_sent && '· D+3'}
                          </div>
                        )}
                        {/* Admin override download buttons (Feb 2026) — bypass
                            the per-user `user_id` scope + payment gate so admin
                            can grab any user's documents for support / pre-call
                            review. Existing endpoints from iter44. */}
                        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {[
                            { path: 'client-estimate.pdf', label: 'Cost Estimate' },
                            { path: 'sow.pdf',             label: 'SoW (PDF)' },
                            { path: 'sow.xlsx',            label: 'SoW (Excel)' },
                            { path: 'materials.pdf',       label: 'Materials' },
                            { path: 'qs-internal.xlsx',    label: 'QS Internal', internal: true },
                          ].map((doc) => (
                            <a
                              key={doc.path}
                              href={`${API_URL}/api/admin/scope/drafts/${d.draft_id}/${doc.path}`}
                              data-testid={`admin-user-doc-${doc.path.replace(/\./g, '-')}-${d.draft_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                background: doc.internal ? '#1F2937' : '#2563EB',
                                color: '#fff',
                                padding: '3px 7px',
                                borderRadius: 4,
                                fontSize: 10,
                                fontWeight: 700,
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              ⬇ {doc.label}
                            </a>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Audit log */}
              <div style={{ marginTop: 18 }} data-testid="admin-user-audit-log">
                <div style={{ fontSize: 11, fontWeight: 800, color: '#64748B', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                  Audit log ({auditLog.length})
                </div>
                {auditLog.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#94A3B8', fontStyle: 'italic' }}>No admin actions on this user yet.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                    {auditLog.map((e, i) => (
                      <div key={i} style={{ fontSize: 11, padding: 8, background: '#F7F8FA', borderRadius: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <strong style={{ color: '#0F2742' }}>{e.action}</strong>
                          <span style={{ color: '#94A3B8' }}>{new Date(e.created_at).toLocaleString('en-GB')}</span>
                        </div>
                        <div style={{ color: '#64748B', marginTop: 2 }}>by <code>{e.actor_email}</code></div>
                        {e.changes && Object.keys(e.changes).length > 0 && (
                          <pre style={{ margin: '4px 0 0', fontSize: 10, color: '#475569', whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(e.changes, null, 2)}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete / GDPR erase modal */}
      {deleteModal && (
        <div
          data-testid="admin-user-delete-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !deleteModal.submitting) closeDeleteModal(); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(15,39,66,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            style={{
              background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520,
              padding: 24, boxShadow: '0 20px 50px -12px rgba(0,0,0,0.35)',
            }}
          >
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0F2742' }}>
              Delete user account
            </h3>
            <p style={{ marginTop: 4, fontSize: 12, color: '#64748B' }}>
              {deleteModal.email || 'This user'} — choose how the account should be removed.
            </p>

            <div style={{ display: 'grid', gap: 10, marginTop: 16 }}>
              <label
                data-testid="admin-user-delete-mode-soft"
                style={{
                  display: 'flex', gap: 10, padding: 12, border: '1px solid',
                  borderColor: deleteModal.mode === 'soft' ? '#0F2742' : '#E2E8F0',
                  borderRadius: 8, cursor: 'pointer',
                  background: deleteModal.mode === 'soft' ? '#F7F8FA' : '#fff',
                }}
              >
                <input
                  type="radio" name="delete-mode" value="soft"
                  checked={deleteModal.mode === 'soft'}
                  onChange={() => setDeleteModal((m) => ({ ...m, mode: 'soft' }))}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#0F2742' }}>Soft delete (suspend)</div>
                  <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
                    Account flagged as suspended. Data retained. User cannot log back in. Reversible by clearing the suspended_at field.
                  </div>
                </div>
              </label>

              <label
                data-testid="admin-user-delete-mode-hard"
                style={{
                  display: 'flex', gap: 10, padding: 12, border: '1px solid',
                  borderColor: deleteModal.mode === 'hard' ? '#991B1B' : '#E2E8F0',
                  borderRadius: 8, cursor: 'pointer',
                  background: deleteModal.mode === 'hard' ? '#FEF2F2' : '#fff',
                }}
              >
                <input
                  type="radio" name="delete-mode" value="hard"
                  checked={deleteModal.mode === 'hard'}
                  onChange={() => setDeleteModal((m) => ({ ...m, mode: 'hard' }))}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#991B1B' }}>
                    Permanently delete (GDPR erasure)
                  </div>
                  <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
                    Emails the user a confirmation, then permanently erases their user record, indicative projects, scope drafts, marketing consents and client sessions. Logs the action to the admin audit log. <strong style={{ color: '#991B1B' }}>Irreversible.</strong>
                  </div>
                </div>
              </label>
            </div>

            {deleteModal.mode === 'hard' && (
              <div style={{ marginTop: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>
                  Type <code>DELETE</code> to confirm
                </label>
                <input
                  type="text"
                  data-testid="admin-user-delete-typed-confirm"
                  value={deleteModal.typedConfirm}
                  onChange={(e) => setDeleteModal((m) => ({ ...m, typedConfirm: e.target.value }))}
                  placeholder="DELETE"
                  style={{
                    marginTop: 6, width: '100%', padding: '8px 12px', fontSize: 13,
                    border: '1px solid #E2E8F0', borderRadius: 8, letterSpacing: '.1em',
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button
                data-testid="admin-user-delete-cancel"
                onClick={closeDeleteModal}
                disabled={deleteModal.submitting}
                className="btn-secondary"
                style={{ fontSize: 13, padding: '9px 16px' }}
              >
                Cancel
              </button>
              {deleteModal.mode === 'soft' ? (
                <button
                  data-testid="admin-user-delete-soft-btn"
                  onClick={confirmDelete}
                  disabled={deleteModal.submitting}
                  style={{
                    fontSize: 13, padding: '9px 16px', background: '#0F2742', color: '#fff',
                    border: 'none', borderRadius: 8, fontWeight: 700,
                    cursor: deleteModal.submitting ? 'wait' : 'pointer',
                    opacity: deleteModal.submitting ? 0.6 : 1,
                  }}
                >
                  {deleteModal.submitting ? 'Suspending…' : 'Suspend account'}
                </button>
              ) : (
                <button
                  data-testid="admin-user-delete-hard-btn"
                  onClick={confirmDelete}
                  disabled={deleteModal.submitting || deleteModal.typedConfirm !== 'DELETE'}
                  style={{
                    fontSize: 13, padding: '9px 16px', background: '#991B1B', color: '#fff',
                    border: 'none', borderRadius: 8, fontWeight: 700,
                    cursor: deleteModal.submitting ? 'wait' : 'pointer',
                    opacity: (deleteModal.submitting || deleteModal.typedConfirm !== 'DELETE') ? 0.5 : 1,
                  }}
                >
                  {deleteModal.submitting ? 'Erasing…' : 'Permanently delete'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
